/**
 * @file index.tsx
 *
 * @fileoverview Creates a group of several form items and displays them
 * together under a single label and description.
 */
import React from 'react';
import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import Grid from "../../../02-atoms/06-layout/04-grid";
import Cell from "../../../02-atoms/06-layout/04-grid/01-cell";
import styles from "./formGroup.module.scss";

const labelCva = cva([styles.label], {
  variants: {
    valid: {
      true: styles.labelSucces,
    },
  },
  defaultVariants: {
    valid: false,
  },
});

const descriptionCva = cva([styles.description], {
  variants: {
    valid: {
      true: styles.descriptionSuccess,
    },
  },
});
export interface FormGroupProps extends VariantProps<typeof labelCva> {
  /**
   * The form elements to be added in the formGroup.
   */
  children: ReactNode;
  /**
   * The label to display in the form group.
   */
  label?: React.ReactNode | string;
  /**
   * Description for the form Group.
   */
  description?: React.ReactNode | string;
  /**
   * The icon to use next to the label.
   */
  icon?: ReactNode;
}

/**
 * The form group component groups several form items together in a group.
 * For longer forms it is recommended to group items together in logical
 * groups so that the user can scan the form more easily.
 */
export default function FormGroup({
  children,
  label,
  description,
  icon,
  valid,
}: FormGroupProps) {
  const childrenOnly = !(label || description || icon);

  return (
    <div className={styles.formGroup}>
      <Grid gap={8} columns={[1, childrenOnly ? 1 : 3]} alignItems="start">
        {!childrenOnly && (
          <Cell gridColumn={1}>
            <div className={labelCva({ valid })}>
              {icon}
              {label}
            </div>
            <div className={descriptionCva({ valid })}>{description}</div>
          </Cell>
        )}
        <Cell gridColumn={[1, childrenOnly ? 1 : "span 2"]}>{children}</Cell>
      </Grid>
    </div>
  );
}

export { FormGroup };
